/**
 * Elite Realty — the house system shared by every flyer in the collection.
 *
 * The five flyers are one brand, not five ideas: the same navy field, the same
 * champagne gold, the same roof mark, the same icon-led specification bar. So
 * the parts live here once and each template only decides composition. That is
 * also what keeps them consistent when the set grows past five.
 *
 * The signature move of the collection is the ANGLED FIELD: a navy (or bone)
 * panel laid over the photograph and rotated a few degrees, so the boundary
 * between type and image is a diagonal rather than a rectangle. It needs no
 * clipping — the panel is simply opaque and rotated, which keeps every piece a
 * plain, draggable, re-colourable element in the editor.
 */

import { SPACE, TYPE, RULE, RADIUS } from '../system/tokens';
import { text, shape, rule, image, svgIcon, qrcode, clipped } from '../system/elements';
import { getIcon } from '../system/icons';
import { fitFontSize } from '../system/typography';

/** The collection's shared identity. */
export const BRAND = {
  name: 'ELITE',
  sub: 'REALTY GROUP',
  tagline: 'EXPERIENCE EXCELLENCE',
  agent: 'MICHAEL TAYLOR',
  agentTitle: 'REAL ESTATE AGENT',
  phone: '512-555-7890',
  email: 'michael.taylor@eliterealty.com',
  web: 'www.eliterealtygroup.com',
  address: ['1234 OAKRIDGE DRIVE', 'AUSTIN, TX 78746'],
  tourUrl: 'https://www.eliterealtygroup.com/virtual-tour',
};

/** The four figures every one of these flyers carries. */
export const SPECS = [
  { icon: 'bed', value: '4', label: 'Bedrooms' },
  { icon: 'bath', value: '3.5', label: 'Bathrooms' },
  { icon: 'area', value: '2,850', label: 'Sq. Ft.' },
  { icon: 'garage', value: '2', label: 'Car Garage' },
];

/** The eight selling points, set as two checked columns. */
export const FEATURES = [
  'Open Concept Living Space',
  'Gourmet Kitchen with Island',
  'High Ceilings & Large Windows',
  'Primary Suite with Walk-in Closet',
  'Spa-like Primary Bathroom',
  'Covered Patio & Outdoor Kitchen',
  'Smart Home Technology',
  'Top Rated Schools Nearby',
];

/** Rotate a point about a centre by `deg`, screen axes (y down, clockwise). */
const rotatePoint = (px, py, cx, cy, deg) => {
  const t = (deg * Math.PI) / 180;
  const cos = Math.cos(t);
  const sin = Math.sin(t);
  const dx = px - cx;
  const dy = py - cy;
  return [cx + dx * cos - dy * sin, cy + dx * sin + dy * cos];
};

/**
 * Angled hero — photograph, then an opaque page-coloured panel rotated over
 * it, then a gold seam riding the panel's edge.
 *
 * Why not clip the photograph to a rotated mask, which is the more obvious
 * reading of "cut the image on a diagonal": this editor's clip support pairs a
 * mask with content occupying the SAME box and rotation (see
 * utils/exportRegressionScenes.js, and the mask flow in CanvasEditor, which
 * masks a shape onto the image beneath it). Point a mask at a differently
 * placed, differently rotated image and the reference fails to resolve — and a
 * `clip-path: url()` that does not resolve makes the element vanish entirely,
 * which is exactly what happened. An opaque panel in the page colour is
 * visually identical to a cut, and it renders.
 *
 * The seam is derived from the panel's rotated edge rather than typed, so it
 * lands on the boundary at any angle.
 */
export const angledField = (idBase, { x, y, width, height, angle, fill, seamColor, seamWidth = RULE.thick }) => {
  const cx = x + width / 2;
  const cy = y + height / 2;
  // The panel's right edge, in page space after rotation. Recorded on the
  // element so the quality gate can check that the type set on this panel
  // clears the diagonal — a headline that crosses the cut and lands on the
  // photograph is the defect this geometry most easily produces.
  const [tx, ty] = rotatePoint(x + width, y, cx, cy, angle);
  const [bx, by] = rotatePoint(x + width, y + height, cx, cy, angle);

  const field = shape(`${idBase}-field`, { x, y, width, height, fill, rotation: angle });
  field.meta = { angledField: { edge: { tx, ty, bx, by } } };
  const els = [field];

  if (seamColor) {
    const midX = (tx + bx) / 2;
    const midY = (ty + by) / 2;
    const len = Math.hypot(bx - tx, by - ty);
    els.push(shape(`${idBase}-seam`, {
      x: Math.round(midX - seamWidth / 2),
      y: Math.round(midY - len / 2),
      width: seamWidth,
      height: Math.round(len),
      fill: seamColor,
      rotation: angle,
    }));
  }

  return els;
};

/**
 * Chevron field — the boundary runs down-right to a point, then back down-left.
 *
 * Built from two angled panels meeting at `apexY`, so it reuses the same edge
 * geometry (and the same gate coverage) as a straight cut. This is a different
 * SHAPE of page, not a recolour of one: the type column widens to the apex and
 * narrows again beneath it.
 */
export const chevronField = (idBase, { x, y, width, height, apexY, angle, fill, seamColor }) => [
  ...angledField(`${idBase}-upper`, {
    x, y, width, height: apexY - y, angle, fill, seamColor,
  }),
  ...angledField(`${idBase}-lower`, {
    x, y: apexY, width, height: y + height - apexY, angle: -angle, fill, seamColor,
  }),
];

/** Roof mark over the ELITE / REALTY GROUP lockup. */
export const logoLockup = (idBase, r, { x, y, width, inkColor, accentColor, align = 'center', tagline = false, markSize = 190 }) => {
  const MARK = markSize;
  const markX = align === 'center' ? x + (width - MARK) / 2 : x;
  const els = [svgIcon(`${idBase}-logo-mark`, {
    x: markX, y, width: MARK, height: MARK * 0.62, content: getIcon('roofMark'), fill: accentColor,
  })];

  let cy = y + MARK * 0.62 + SPACE.xs;
  // Sizes are fitted to the box the caller gave, not assumed: the lockup is
  // set wide-tracked, and tracking is what pushes it past a narrow column.
  const name = text(`${idBase}-logo-name`, r.h2, BRAND.name, { x, y: cy, width, align },
    { color: inkColor, letterSpacing: 30, fontSize: fitFontSize([BRAND.name], { ...r.h2, letterSpacing: 30 }, width, 60) });
  els.push(name);
  cy += name.height + SPACE.xxs;

  const sub = text(`${idBase}-logo-sub`, r.statLabel, BRAND.sub, { x, y: cy, width, align },
    { color: accentColor, letterSpacing: 16, fontSize: fitFontSize([BRAND.sub], { ...r.statLabel, letterSpacing: 16 }, width, 22) });
  els.push(sub);
  cy += sub.height;

  if (tagline) {
    cy += SPACE.xs;
    els.push(rule(`${idBase}-logo-rule`, {
      x: align === 'center' ? x + width / 2 - 130 : x, y: cy, width: 260, weight: RULE.hair, color: accentColor,
    }));
    cy += SPACE.xs;
    const tagSize = fitFontSize([BRAND.tagline], { ...r.micro, letterSpacing: 10 }, width, 20);
    els.push(text(`${idBase}-logo-tagline`, r.micro, BRAND.tagline, { x, y: cy, width, align },
      { color: accentColor, letterSpacing: 10, fontSize: tagSize }));
    cy += tagSize * 1.4;
  }

  return { elements: els, y: cy };
};

/**
 * Price plate with a downward pennant point — the "OFFERED AT $789,000" tag
 * that anchors the top-right of every flyer in the set.
 */
export const priceTag = (idBase, r, { x, y, width, label, price, fill, inkColor, pennant = true, priceSize = 150 }) => {
  const H = 300;
  const els = [shape(`${idBase}-price-plate`, { x, y, width, height: H, fill })];

  if (pennant) {
    // A triangle rotated a half-turn reads as the tag's point.
    els.push(shape(`${idBase}-price-point`, {
      x: x + width / 2 - 90, y: y + H - 2, width: 180, height: 90,
      kind: 'triangle', fill, rotation: 180,
    }));
  }

  els.push(text(`${idBase}-price-label`, r.statLabel, label, { x, y: y + SPACE.sm, width, align: 'center' },
    { color: inkColor }));
  els.push(text(`${idBase}-price-value`, r.h1, price, {
    x, y: y + SPACE.sm + TYPE.caption * 1.4 + SPACE.xxs, width, align: 'center',
  }, { color: inkColor, fontSize: priceSize }));

  return { elements: els, y: y + H + (pennant ? 90 : 0) };
};

/**
 * Specification bar — four icon-led figures with hairline dividers. This is
 * the block that most distinguishes the collection from a plain text flyer,
 * and every icon stays an editable `svg` element.
 */
export const specBar = (idBase, r, { x, y, width, specs = SPECS, iconColor, valueColor, labelColor, dividerColor, iconInCircle = false, circleFill, iconSize = 80, valueSize }) => {
  const cols = specs.length;
  const gutter = SPACE.sm;
  const colW = Math.floor((width - gutter * (cols - 1)) / cols);
  const ICON = iconSize;
  const els = [];
  let bottom = y;

  specs.forEach((s, i) => {
    const cx = x + i * (colW + gutter);
    const iconX = cx + (colW - ICON) / 2;

    if (iconInCircle) {
      const D = ICON + SPACE.sm;
      els.push(shape(`${idBase}-spec${i}-disc`, {
        x: cx + (colW - D) / 2, y, width: D, height: D, kind: 'circle', fill: circleFill,
      }));
      els.push(svgIcon(`${idBase}-spec${i}-icon`, {
        x: iconX, y: y + SPACE.sm / 2, width: ICON, height: ICON, content: getIcon(s.icon), fill: iconColor,
      }));
    } else {
      els.push(svgIcon(`${idBase}-spec${i}-icon`, {
        x: iconX, y, width: ICON, height: ICON, content: getIcon(s.icon), fill: iconColor,
      }));
    }

    const discH = iconInCircle ? ICON + SPACE.sm : ICON;
    const valueY = y + discH + SPACE.xs;
    const value = text(`${idBase}-spec${i}-value`, r.stat, s.value, { x: cx, y: valueY, width: colW, align: 'center' },
      { color: valueColor, ...(valueSize ? { fontSize: valueSize } : {}) });
    const labelY = valueY + value.height + SPACE.xxs;
    const label = text(`${idBase}-spec${i}-label`, r.statLabel, s.label, { x: cx, y: labelY, width: colW, align: 'center' },
      { color: labelColor });
    els.push(value, label);
    bottom = Math.max(bottom, labelY + label.height);

    if (i > 0 && dividerColor) {
      els.push(shape(`${idBase}-spec${i}-div`, {
        x: cx - Math.round(gutter / 2), y: y + SPACE.xs, width: RULE.hair,
        height: discH + value.height + SPACE.xs, fill: dividerColor,
      }));
    }
  });

  return { elements: els, y: bottom };
};

/**
 * Interior photo strip with angled dividers — the horizontal band of room
 * shots. Photos are laid edge to edge and the diagonals are cut in by thin
 * rotated slivers in the page colour, which keeps each photograph an ordinary
 * replaceable image.
 */
export const photoStrip = (idBase, { x, y, width, height, photos, gap = 16, boxHeight = 700, cut = true }) => {
  const n = photos.length;

  // `cut: false` butts square photographs edge to edge — one of the reference
  // sheets runs its strip that way, and a strip that is angled on every sheet
  // is one of the things that made them read as the same page.
  if (!cut) {
    const slice = Math.ceil(width / n);
    return {
      elements: photos.map((src, i) => image(`${idBase}-strip${i}`, {
        x: x + i * slice, y, width: i === n - 1 ? width - i * slice : slice, height, src,
      })),
      y: y + height,
    };
  }

  // Each photograph is genuinely CUT to a parallelogram, not overlaid with a
  // line. The mask shares the image's exact box and rotation, which is the
  // only arrangement this editor resolves (see `clipped` in system/elements).
  //
  // The editor's parallelogram skews by a fixed 25% of its width, so the slant
  // ANGLE is set by the box proportions: a taller box gives a shallower cut.
  // The box therefore runs well above and below the visible band — the bands
  // painted after this one cover the overhang.
  //
  // A parallelogram has constant horizontal width (w − skew) at every height,
  // so laying them at a pitch of (w − skew + gap) makes the visible slices
  // tile with a parallel gap of exactly `gap` all the way down.
  const skewRatio = 0.25;
  const boxW = Math.round((width + 200 - gap * (n - 1)) / (n * (1 - skewRatio) + skewRatio));
  const skew = boxW * skewRatio;
  const pitch = boxW - skew + gap;
  const x0 = Math.round(x - 100);
  const boxY = Math.round(y + (height - boxHeight) / 2);

  const els = [];
  photos.forEach((src, i) => {
    const [mask, photo] = clipped(
      `${idBase}-strip${i}-mask`,
      image(`${idBase}-strip${i}`, {
        x: Math.round(x0 + i * pitch), y: boxY, width: boxW, height: boxHeight, src,
      }),
      'parallelogram',
    );
    els.push(mask, photo);
  });

  return { elements: els, y: y + height };
};

/** "PROPERTY FEATURES" heading over two checked columns. */
export const featureList = (idBase, r, { x, y, width, title = 'Property Features', items = FEATURES, titleColor, checkColor, itemColor, ruleColor, itemRole = r.body, rowGap = SPACE.xs, titleSize }) => {
  const els = [];
  const heading = text(`${idBase}-feat-title`, r.h3, title, { x, y, width },
    { color: titleColor, letterSpacing: 6, ...(titleSize ? { fontSize: titleSize } : {}) });
  els.push(heading);
  let cy = y + heading.height + SPACE.xxs;

  if (ruleColor) {
    els.push(rule(`${idBase}-feat-rule`, { x, y: cy, width: 300, weight: RULE.medium, color: ruleColor }));
    cy += RULE.medium;
  }
  cy += SPACE.sm;

  const colW = Math.floor((width - SPACE.lg) / 2);
  const CHECK = 40;
  // One size for the whole list, fitted to its longest entry — a list where a
  // single row is a step smaller than its neighbours reads as a mistake.
  const itemW = colW - CHECK - SPACE.xs;
  const fitted = { ...itemRole, fontSize: fitFontSize(items, itemRole, itemW, 26) };
  const rowH = fitted.fontSize * fitted.lineHeight + rowGap;

  items.forEach((item, i) => {
    const col = i % 2;
    const row = Math.floor(i / 2);
    const cx = x + col * (colW + SPACE.lg);
    const ry = cy + row * rowH;
    els.push(svgIcon(`${idBase}-feat${i}-check`, {
      x: cx, y: ry + 6, width: CHECK, height: CHECK, content: getIcon('check'), fill: checkColor,
    }));
    els.push(text(`${idBase}-feat${i}`, fitted, item, {
      x: cx + CHECK + SPACE.xs, y: ry, width: itemW,
    }, { color: itemColor }));
  });

  return { elements: els, y: cy + Math.ceil(items.length / 2) * rowH };
};

/** QR code beside its two-line caption. */
export const qrBlock = (idBase, r, { x, y, size = 300, capW = 420, captionSize = 56, lineTop, lineBottom, topColor, bottomColor, qrColor = '#0A1A2F', captionAlign = 'left' }) => {
  const els = [qrcode(`${idBase}-qr`, { x, y, size, data: BRAND.tourUrl, color: qrColor, bgColor: '#FFFFFF' })];
  const capX = x + size + SPACE.sm;
  // Both caption lines are set at one size, fitted to the narrower of the two.
  const size2 = fitFontSize([lineTop, lineBottom], { ...r.h3, letterSpacing: 4, fontSize: captionSize }, capW, 28);
  // Ceiled: `text()` rounds its box height up, so a fractional line height
  // would leave the two captions overlapping by a pixel.
  const lineH = Math.ceil(size2 * 1.25);
  const topY = Math.round(y + size / 2 - lineH);

  els.push(text(`${idBase}-qr-top`, r.h3, lineTop, { x: capX, y: topY, width: capW, align: captionAlign },
    { color: topColor, letterSpacing: 4, fontSize: size2 }));
  els.push(text(`${idBase}-qr-bottom`, r.h3, lineBottom, {
    x: capX, y: topY + lineH, width: capW, align: captionAlign,
  }, { color: bottomColor, letterSpacing: 4, fontSize: size2 }));

  return { elements: els, y: y + size, width: size + SPACE.sm + capW };
};

/** A contact line: icon, then copy. */
export const contactRow = (id, r, { x, y, width, icon, copy, iconColor, textColor, size = 46, role = r.body, gap = SPACE.xs }) => [
  svgIcon(`${id}-icon`, { x, y: y + 4, width: size, height: size, content: getIcon(icon), fill: iconColor }),
  text(`${id}-copy`, role, copy, { x: x + size + gap, y, width: width - size - gap }, { color: textColor }),
];

/** Circular agent headshot with a thin ring. */
export const agentPortrait = (idBase, { x, y, size, src, ringColor }) => [
  shape(`${idBase}-agent-ring`, {
    x: x - RULE.thin, y: y - RULE.thin, width: size + RULE.thin * 2, height: size + RULE.thin * 2,
    kind: 'circle', fill: ringColor,
  }),
  image(`${idBase}-agent-photo`, { x, y, width: size, height: size, src, cornerRadius: size / 2 }),
];

export { RADIUS };
